import { Page, TextField, Modal, Select } from '../components/forms';
import { useEffect, useState, useCallback } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  DataTable,
  Pagination,
  Spinner,
  Banner,
  Button,
  FormLayout,
} from '@shopify/polaris';
import { PlusIcon, EditIcon, DeleteIcon } from '@shopify/polaris-icons';
import { useApi } from '../hooks/useApi';

interface User {
  id: number;
  email: string;
  name: string;
  picture: string | null;
  shop_count: number;
  shops: string | null;
  organizations: string | null;
  created_at: string;
}

interface UserDetail {
  id: number;
  email: string;
  name: string;
  picture: string | null;
  created_at: string;
}

interface OrgMembership {
  membership_id: number;
  organization_id: string;
  organization_name: string;
  organization_slug: string;
  role: string;
  status: string;
  joined_at: string;
}

interface ShopMembership {
  membership_id: number;
  shop_id: string;
  shop_name: string;
  shop_domain: string;
  role: string;
  joined_at: string;
}

interface UsersResponse {
  success: boolean;
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface UserDetailResponse {
  success: boolean;
  user: UserDetail;
  organizations: OrgMembership[];
  shops: ShopMembership[];
}

interface Organization {
  id: string;
  name: string;
  slug: string;
}

interface Shop {
  id: string;
  name: string;
  domain: string;
}

const ORG_ROLES = ['owner', 'admin', 'member'];
const SHOP_ROLES = ['admin', 'manager', 'member', 'viewer'];

export default function UsersList() {
  const { fetchApi, error } = useApi();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [search, setSearch] = useState('');

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserDetail | null>(null);
  const [editingOrgMemberships, setEditingOrgMemberships] = useState<Array<{ organization_id: string; role: string }>>([]);
  const [editingShopMemberships, setEditingShopMemberships] = useState<Array<{ shop_id: string; role: string }>>([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editName, setEditName] = useState('');

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Available orgs and shops for dropdowns
  const [allOrgs, setAllOrgs] = useState<Organization[]>([]);
  const [allShops, setAllShops] = useState<Shop[]>([]);

  const loadUsers = useCallback(async () => {
    setLoading(true);

    const params = new URLSearchParams();
    params.append('page', pagination.page.toString());
    params.append('limit', pagination.limit.toString());
    if (search) params.append('search', search);

    const response = await fetchApi<UsersResponse>(`/super-admin/users?${params.toString()}`);

    if (response?.success) {
      setUsers(response.users);
      setPagination(response.pagination);
    }

    setLoading(false);
  }, [fetchApi, pagination.page, search]);

  const loadOrgsAndShops = useCallback(async () => {
    // Load all organizations
    const orgsResponse = await fetchApi<{ success: boolean; customers: Organization[] }>('/super-admin/customers?limit=100');
    if (orgsResponse?.success) {
      setAllOrgs(orgsResponse.customers);
    }

    // Load all shops
    const shopsResponse = await fetchApi<{ success: boolean; shops: Shop[] }>('/super-admin/shops?limit=100');
    if (shopsResponse?.success) {
      setAllShops(shopsResponse.shops);
    }
  }, [fetchApi]);

  useEffect(() => {
    loadUsers();
    loadOrgsAndShops();
  }, [loadUsers, loadOrgsAndShops]);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString();
  };

  // Open edit modal and load user details
  const handleEdit = async (user: User) => {
    setEditLoading(true);
    setEditModalOpen(true);

    const response = await fetchApi<UserDetailResponse>(`/super-admin/users/${user.id}`);

    if (response?.success) {
      setEditingUser(response.user);
      setEditName(response.user.name || '');
      setEditingOrgMemberships(
        response.organizations.map(om => ({
          organization_id: om.organization_id,
          role: om.role,
        }))
      );
      setEditingShopMemberships(
        response.shops.map(sm => ({
          shop_id: sm.shop_id,
          role: sm.role,
        }))
      );
    }

    setEditLoading(false);
  };

  // Save user changes
  const handleSaveUser = async () => {
    if (!editingUser) return;

    setEditLoading(true);

    const response = await fetchApi<{ success: boolean; error?: string }>(`/super-admin/users/${editingUser.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name: editName,
        orgMemberships: editingOrgMemberships,
        shopMemberships: editingShopMemberships,
      }),
    });

    if (response?.success) {
      setEditModalOpen(false);
      setEditingUser(null);
      loadUsers();
    } else {
      alert(response?.error || 'Failed to save user');
    }

    setEditLoading(false);
  };

  // Open delete confirmation
  const handleDelete = (user: User) => {
    setDeletingUser(user);
    setDeleteModalOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!deletingUser) return;

    setDeleteLoading(true);

    const response = await fetchApi<{ success: boolean; error?: string }>(`/super-admin/users/${deletingUser.id}`, {
      method: 'DELETE',
    });

    if (response?.success) {
      setDeleteModalOpen(false);
      setDeletingUser(null);
      loadUsers();
    } else {
      alert(response?.error || 'Failed to delete user');
    }

    setDeleteLoading(false);
  };

  // Add org membership
  const handleAddOrgMembership = () => {
    if (allOrgs.length === 0) return;
    const firstAvailable = allOrgs.find(o => !editingOrgMemberships.some(m => m.organization_id === o.id));
    if (firstAvailable) {
      setEditingOrgMemberships([...editingOrgMemberships, { organization_id: firstAvailable.id, role: 'member' }]);
    }
  };

  // Remove org membership
  const handleRemoveOrgMembership = (index: number) => {
    setEditingOrgMemberships(editingOrgMemberships.filter((_, i) => i !== index));
  };

  // Add shop membership
  const handleAddShopMembership = () => {
    if (allShops.length === 0) return;
    const firstAvailable = allShops.find(s => !editingShopMemberships.some(m => m.shop_id === s.id));
    if (firstAvailable) {
      setEditingShopMemberships([...editingShopMemberships, { shop_id: firstAvailable.id, role: 'member' }]);
    }
  };

  // Remove shop membership
  const handleRemoveShopMembership = (index: number) => {
    setEditingShopMemberships(editingShopMemberships.filter((_, i) => i !== index));
  };

  const rows = users.map((user) => [
    <InlineStack key={user.id} gap="300" blockAlign="center">
      {user.picture ? (
        <img
          src={user.picture}
          alt={user.name}
          style={{ width: 32, height: 32, borderRadius: '50%' }}
        />
      ) : (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: '#e4e5e7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {user.name?.charAt(0).toUpperCase() || 'U'}
        </div>
      )}
      <BlockStack gap="100">
        <Text as="span" fontWeight="semibold">{user.name || 'Unknown'}</Text>
        <Text as="span" tone="subdued" variant="bodySm">{user.email}</Text>
      </BlockStack>
    </InlineStack>,
    <BlockStack key={`orgs-${user.id}`} gap="100">
      {user.organizations ? (
        <Text as="span" variant="bodySm">{user.organizations}</Text>
      ) : (
        <Badge tone="attention">No organization</Badge>
      )}
    </BlockStack>,
    <BlockStack key={`shops-${user.id}`} gap="100">
      {user.shops ? (
        <Text as="span" variant="bodySm">{user.shops}</Text>
      ) : (
        <Badge tone="attention">No shops</Badge>
      )}
    </BlockStack>,
    formatDate(user.created_at),
    <InlineStack key={`actions-${user.id}`} gap="200">
      <Button
        icon={EditIcon}
        onClick={() => handleEdit(user)}
        accessibilityLabel="Edit user"
        size="slim"
      />
      <Button
        icon={DeleteIcon}
        onClick={() => handleDelete(user)}
        accessibilityLabel="Delete user"
        size="slim"
        tone="critical"
      />
    </InlineStack>,
  ]);

  return (
    <Page title="Users" subtitle={`${pagination.total} total users`}>
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" wrap={false} blockAlign="end">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Search"
                  value={search}
                  onChange={setSearch}
                  placeholder="Search by email or name..."
                  autoComplete="off"
                  clearButton
                  onClearButtonClick={() => setSearch('')}
                />
              </div>
              <Button onClick={handleSearch} variant="primary">Search</Button>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Table */}
        <Layout.Section>
          <Card>
            {loading ? (
              <InlineStack align="center">
                <Spinner size="large" />
              </InlineStack>
            ) : users.length === 0 ? (
              <BlockStack gap="200" inlineAlign="center">
                <Text as="p" tone="subdued">No users found</Text>
              </BlockStack>
            ) : (
              <>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                  headings={['User', 'Organizations', 'Shops', 'Joined', 'Actions']}
                  rows={rows}
                />
                <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    hasPrevious={pagination.page > 1}
                    hasNext={pagination.page < pagination.totalPages}
                    onPrevious={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    onNext={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  />
                </div>
              </>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Edit Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => { setEditModalOpen(false); setEditingUser(null); }}
        title={editingUser ? `Edit User: ${editingUser.email}` : 'Edit User'}
        primaryAction={{
          content: 'Save',
          onAction: handleSaveUser,
          loading: editLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => { setEditModalOpen(false); setEditingUser(null); },
          },
        ]}
        size="large"
      >
        <Modal.Section>
          {editLoading && !editingUser ? (
            <InlineStack align="center">
              <Spinner size="large" />
            </InlineStack>
          ) : editingUser ? (
            <BlockStack gap="400">
              <FormLayout>
                <TextField
                  label="Name"
                  requiredIndicator value={editName}
                  onChange={setEditName}
                  autoComplete="off"
                />
                <TextField
                  label="Email"
                  value={editingUser.email}
                  disabled
                  autoComplete="off"
                />
              </FormLayout>

              {/* Organization Memberships */}
              <BlockStack gap="300">
                <InlineStack align="space-between">
                  <Text as="h3" variant="headingMd">Organization Memberships</Text>
                  <Button icon={PlusIcon} onClick={handleAddOrgMembership} size="slim">Add Organization</Button>
                </InlineStack>
                {editingOrgMemberships.length === 0 ? (
                  <Text as="p" tone="subdued">No organization memberships</Text>
                ) : (
                  editingOrgMemberships.map((membership, index) => (
                    <InlineStack key={index} gap="300" blockAlign="end">
                      <div style={{ flex: 1 }}>
                        <Select
                          label="Organization"
                          labelHidden
                          options={allOrgs.map(o => ({ label: o.name, value: o.id }))}
                          requiredIndicator value={membership.organization_id}
                          onChange={(value) => {
                            const updated = [...editingOrgMemberships];
                            updated[index].organization_id = value;
                            setEditingOrgMemberships(updated);
                          }}
                        />
                      </div>
                      <div style={{ width: 120 }}>
                        <Select
                          label="Role"
                          labelHidden
                          options={ORG_ROLES.map(r => ({ label: r, value: r }))}
                          requiredIndicator value={membership.role}
                          onChange={(value) => {
                            const updated = [...editingOrgMemberships];
                            updated[index].role = value;
                            setEditingOrgMemberships(updated);
                          }}
                        />
                      </div>
                      <Button
                        icon={DeleteIcon}
                        onClick={() => handleRemoveOrgMembership(index)}
                        accessibilityLabel="Remove"
                        tone="critical"
                        size="slim"
                      />
                    </InlineStack>
                  ))
                )}
              </BlockStack>

              {/* Shop Memberships */}
              <BlockStack gap="300">
                <InlineStack align="space-between">
                  <Text as="h3" variant="headingMd">Shop Memberships</Text>
                  <Button icon={PlusIcon} onClick={handleAddShopMembership} size="slim">Add Shop</Button>
                </InlineStack>
                {editingShopMemberships.length === 0 ? (
                  <Text as="p" tone="subdued">No shop memberships</Text>
                ) : (
                  editingShopMemberships.map((membership, index) => (
                    <InlineStack key={index} gap="300" blockAlign="end">
                      <div style={{ flex: 1 }}>
                        <Select
                          label="Shop"
                          labelHidden
                          options={allShops.map(s => ({ label: s.name || s.domain, value: s.id }))}
                          requiredIndicator value={membership.shop_id}
                          onChange={(value) => {
                            const updated = [...editingShopMemberships];
                            updated[index].shop_id = value;
                            setEditingShopMemberships(updated);
                          }}
                        />
                      </div>
                      <div style={{ width: 120 }}>
                        <Select
                          label="Role"
                          labelHidden
                          options={SHOP_ROLES.map(r => ({ label: r, value: r }))}
                          requiredIndicator value={membership.role}
                          onChange={(value) => {
                            const updated = [...editingShopMemberships];
                            updated[index].role = value;
                            setEditingShopMemberships(updated);
                          }}
                        />
                      </div>
                      <Button
                        icon={DeleteIcon}
                        onClick={() => handleRemoveShopMembership(index)}
                        accessibilityLabel="Remove"
                        tone="critical"
                        size="slim"
                      />
                    </InlineStack>
                  ))
                )}
              </BlockStack>
            </BlockStack>
          ) : null}
        </Modal.Section>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeletingUser(null); }}
        title="Delete User"
        primaryAction={{
          content: 'Delete',
          onAction: handleConfirmDelete,
          loading: deleteLoading,
          destructive: true,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => { setDeleteModalOpen(false); setDeletingUser(null); },
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p">
              Are you sure you want to delete <strong>{deletingUser?.name || deletingUser?.email}</strong>?
            </Text>
            <Banner tone="warning">
              This will permanently delete the user and remove all their organization and shop memberships.
              This action cannot be undone.
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
